import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import i18next from 'i18next';
import { Gpt } from 'react-gpt-ads';
import { IoShareSocialOutline } from 'react-icons/io5';
import zhConvertor from 'zhconvertor';
import { ChartNP, Loader, NewsRollDrawer, PageMeta, Party, StateFlagsNav } from '../index';
import {
  Johor,
  Kedah,
  Kelantan,
  KL,
  Melaka,
  Sembilan,
  Pahang,
  Penang,
  Perak,
  Perlis,
  Sabah,
  Sarawak,
  Selangor,
  Terengganu,
} from '../../map';
import CandidateBar from './CandidateBar';
import VoteUpdates from '../VoteUpdates/VoteUpdates';
import SeatDemographics from './SeatDemographics';
import SeatInfoPanel from './SeatInfoPanel';
import PartyArticlesPanel from './PartyArticlesPanel';
import RelatedNewsPanel from './RelatedNewsPanel';
import { getBgColorByParty, derivePartyRows, deriveFillsFromSeats } from './seatHelpers';
import { getStateBySlug } from '../../config/states';
import { trackEvent } from '../../utils/analytics';
import { recordSeat } from '../../utils/interestTracker';
import './SeatsDetail.css';

const SHARE_TITLE = '2026柔佛州选 议席列表 - 东方网';
const SHARE_TEXT = '获取2026柔佛州选的最新消息';

const FALLBACK_NAMES = {
  johor: '柔佛',
  kedah: '吉打',
  penang: '槟城',
  kelantan: '吉兰丹',
  melaka: '马六甲',
  negerisembilan: '森美兰',
  selangor: '雪兰莪',
  sabah: '沙巴',
  terengganu: '登嘉楼',
  sarawak: '砂拉越',
  perlis: '玻璃市',
  perak: '霹雳',
  pahang: '彭亨',
  kl: '联邦直辖区',
};

const stateLabel = (slug) => {
  const cfg = getStateBySlug(slug);
  if (cfg) return `${i18next.t(cfg.nameKey)}（${cfg.totalSeats} 席）`;
  return i18next.t(FALLBACK_NAMES[slug]) || slug;
};

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

// Builds a GA4-friendly seat label like "Johor N1" from the state slug + seatname.
const seatLabel = (state, seatname) =>
  `${state ? state.charAt(0).toUpperCase() + state.slice(1) : ''} ${seatname}`.trim();

// Renders an inline GPT ad, but unmounts itself when the slot comes back
// empty so the surrounding spacing / full-width grid break collapses.
const SeatInlineAd = ({ index }) => {
  const name = `div-gpt-ad-middle-${index}`;
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    const gt = (window.googletag = window.googletag || { cmd: [] });
    const onRenderEnded = (event) => {
      if (
        event.slot &&
        event.slot.getSlotElementId() === name &&
        event.isEmpty
      ) {
        setEmpty(true);
      }
    };
    gt.cmd.push(() => {
      gt.pubads().addEventListener('slotRenderEnded', onRenderEnded);
    });
    return () => {
      gt.cmd.push(() => {
        gt.pubads().removeEventListener('slotRenderEnded', onRenderEnded);
      });
    };
  }, [name]);

  if (empty) return null;

  return (
    <div className="seat_inline_ad">
      <Gpt
        adUnit="/2163006/election_336x280_300x250"
        name={name}
        size={[[300, 250], [336, 280], 'fluid']}
      />
    </div>
  );
};

// Renders a seat's vote stats + candidate bars. Shared by the current-election
// body and the inline "last election" block so both have one rendering path.
const SeatResultBody = ({ seat }) => {
  if (!seat || !seat.candidate || seat.candidate.length === 0) return null;
  return (
    <div className="seat_info_grid">
      <div className="seat_info_left">{tr('投票情况')}</div>
      <div className="seat_info_right">
        {parseInt(seat.total_votes) > 0 && (
          <>
            <span className="info_numbers">
              {tr('投票人数')}: {seat.total_votes.toLocaleString()}
              {parseFloat(seat.percentage) > 0 && (
                <span>{` (${seat.percentage}%)`}</span>
              )}
            </span>
            <br />
          </>
        )}
        {parseInt(seat.majority) > 0 && (
          <span className="info_numbers">
            {tr('多数票')}: {parseInt(seat.majority).toLocaleString()}
          </span>
        )}
        {parseInt(seat.invalid) > 0 && (
          <span className="info_numbers">
            {tr('废票')}: {parseInt(seat.invalid).toLocaleString()}
          </span>
        )}
        <div className="bar_chart_container">
          {seat.candidate.map((eachCandidate, idx) => (
            <CandidateBar
              key={idx}
              candidate={eachCandidate}
              totalVotes={seat.total_votes}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SeatsDetail = () => {
  const { parliment, state } = useParams();
  const { hash } = useLocation();
  const [seatCode, setSeatCode] = useState(parliment);
  const [data, setData] = useState([]);
  const [done, setDone] = useState(false);
  const [allCollapsed, setAllCollapsed] = useState(true);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [selectedSeatName, setSelectedSeatName] = useState(null);
  // Previous-election results: a seatname -> seat map (null until fetched), a
  // loading flag, and the set of seat indices whose last-election block is open.
  const [lastData, setLastData] = useState(null);
  const [lastLoading, setLastLoading] = useState(false);
  const [lastOpenSet, setLastOpenSet] = useState(() => new Set());
  // Constituency name (ednamec) whose related-news panel is open; null = closed.
  const [newsTag, setNewsTag] = useState(null);
  // Seat list filters: alliance_en / party_eng keys; null = no filter.
  const [filterAlliance, setFilterAlliance] = useState(null);
  const [filterParty, setFilterParty] = useState(null);

  const selectedSeatData = selectedSeatName
    ? data.find((s) => s.seatname === selectedSeatName)
    : null;

  // Filter chip options, derived from the candidates in the current seat data.
  // Sorted by label so the chip order stays stable across the 15s data polls.
  const allianceOptions = useMemo(() => {
    const map = new Map();
    data.forEach((seat) =>
      (seat.candidate || []).forEach((c) => {
        if (c.alliance_en && !map.has(c.alliance_en)) {
          map.set(c.alliance_en, {
            key: c.alliance_en,
            label: c.alliance || c.alliance_en,
            logo: c.alliance_logo || '',
          });
        }
      })
    );
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'zh'));
  }, [data]);

  // Linked to the alliance filter: only parties fielded under that alliance.
  const partyOptions = useMemo(() => {
    const map = new Map();
    data.forEach((seat) =>
      (seat.candidate || []).forEach((c) => {
        if (!c.party_eng || map.has(c.party_eng)) return;
        if (filterAlliance && c.alliance_en !== filterAlliance) return;
        map.set(c.party_eng, {
          key: c.party_eng,
          label: c.party || c.party_eng,
          allianceKey: c.alliance_en,
          logo:
            (c.party_eng || '').toUpperCase() === 'BEBAS'
              ? c.bebas_logo || ''
              : c.party_logo || '',
        });
      })
    );
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'zh'));
  }, [data, filterAlliance]);

  const seatMatches = (seat) =>
    (!filterAlliance && !filterParty) ||
    (seat.candidate || []).some(
      (c) =>
        (!filterAlliance || c.alliance_en === filterAlliance) &&
        (!filterParty || c.party_eng === filterParty)
    );

  const visibleCount = data.filter(seatMatches).length;

  const selectAlliance = (key) => {
    const next = key === filterAlliance ? null : key;
    setFilterAlliance(next);
    // Drop the party filter when it no longer belongs to the chosen alliance.
    if (
      filterParty &&
      next &&
      !data.some((seat) =>
        (seat.candidate || []).some(
          (c) => c.party_eng === filterParty && c.alliance_en === next
        )
      )
    ) {
      setFilterParty(null);
    }
    if (next) trackEvent('seat_filter', { state, alliance: next });
  };

  const selectParty = (key) => {
    const next = key === filterParty ? null : key;
    setFilterParty(next);
    if (next) trackEvent('seat_filter', { state, alliance: filterAlliance || '', party: next });
  };

  useEffect(() => {
    let cancelled = false;
    setSeatCode(parliment);
    window.scrollTo(0, 0);
    setAllCollapsed(true);
    setLastData(null);
    setLastLoading(false);
    setLastOpenSet(new Set());
    setFilterAlliance(null);
    setFilterParty(null);

    const fetchData = () => {
      const lang = localStorage.getItem('lang') || 'zh_CN';
      axios
        .get(`public-api/seatinfo?seat=${state}&code=${parliment}&lang=${lang}`)
        .then((res) => {
          if (cancelled) return;
          const datas = res.data || [];
          setData(datas);
          setDone(true);
        })
        .catch(() => {
          if (!cancelled) setDone(true);
        });
    };

    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state, parliment]);

  // Coalition tally (drives both the <Party> table and the map legend) and the
  // map fill colors — both derived from the seatinfo payload, so this page
  // polls one endpoint instead of three (allPartyResult/getInfo dropped).
  const partyRows = useMemo(() => derivePartyRows(data), [data]);
  const mapFills = useMemo(() => deriveFillsFromSeats(data), [data]);

  useEffect(() => {
    if (!allCollapsed) {
      setOpenSet(new Set(data.map((_, i) => i)));
    }
  }, [data, allCollapsed]);

  // Scroll to (and expand) the seat referenced by the URL hash once data has
  // loaded. The HashLink in the search box can't reach the target on its own
  // because the seat sections render only after this async fetch resolves. A
  // ref tracks the handled hash so the 15s poll (which replaces `data`) doesn't
  // keep yanking the user back to the seat.
  const handledHashRef = useRef(null);
  useEffect(() => {
    if (!done || !hash || data.length === 0) return;
    if (handledHashRef.current === hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const idx = data.findIndex((s) => `s${s.seatname}` === id);
    if (idx === -1) return;
    handledHashRef.current = hash;
    setOpenSet((prev) => new Set(prev).add(idx));
    // Re-attempt the scroll across ~1s: the panel expansion re-renders and the
    // map SVG / GPT ads above the target load late, each shifting layout and
    // pushing the seat away from a single one-shot scroll.
    const timers = [0, 150, 400, 800].map((delay) =>
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [done, hash, data]);

  // Fetch the previous election's seats once; one call covers every seat in the
  // current scope, so later button clicks just toggle from the cached map.
  const fetchLastElection = () => {
    if (lastData || lastLoading) return;
    setLastLoading(true);
    const lang = localStorage.getItem('lang') || 'zh_CN';
    axios
      .get(
        `public-api/seatinfo?seat=${state}&code=${parliment}&lang=${lang}&last_election=true`
      )
      .then((res) => {
        const map = {};
        (res.data || []).forEach((s) => {
          map[s.seatname] = s;
        });
        setLastData(map);
      })
      .catch(() => setLastData({}))
      .finally(() => setLastLoading(false));
  };

  const toggleLast = (idx, seatname) => {
    fetchLastElection();
    setLastOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else {
        next.add(idx);
        trackEvent('seat_last_election', { seat: seatLabel(state, seatname) });
      }
      return next;
    });
  };

  const togglePanel = (idx, seatname) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else {
        next.add(idx);
        trackEvent('seat_section_expand', { seat: seatLabel(state, seatname) });
      }
      return next;
    });
  };

  // Map seat click: track which seat, then open the seat info panel as before.
  const handleMapSeatClick = (seatname) => {
    trackEvent('map_seat_click', { seat: seatLabel(state, seatname) });
    const seatRow = data.find((s) => s.seatname === seatname);
    recordSeat({ slug: state, seatname, label: seatRow && seatRow.ednamec ? seatRow.ednamec : seatname });
    setSelectedSeatName(seatname);
  };

  // Panel's "view seat" button: close the panel, then expand and scroll to the
  // seat's section on this page (clearing filters that would hide it).
  const handleViewSeatSection = () => {
    const seatname = selectedSeatName;
    setSelectedSeatName(null);
    const idx = data.findIndex((s) => s.seatname === seatname);
    if (idx === -1) return;
    if (!seatMatches(data[idx])) {
      setFilterAlliance(null);
      setFilterParty(null);
    }
    setOpenSet((prev) => new Set(prev).add(idx));
    // Retried scroll, same as the hash effect: expansion re-renders shift layout.
    [0, 150, 400].forEach((delay) =>
      setTimeout(() => {
        const el = document.getElementById(`s${seatname}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, delay)
    );
  };

  // Vote-update line click: opens the seat info panel, same as a map click.
  const handleUpdateClick = (seatname) => {
    trackEvent('vote_update_click', { seat: seatLabel(state, seatname) });
    const seatRow = data.find((s) => s.seatname === seatname);
    recordSeat({ slug: state, seatname, label: seatRow && seatRow.ednamec ? seatRow.ednamec : seatname });
    setSelectedSeatName(seatname);
  };

  const toggleAll = () => {
    if (allCollapsed) {
      trackEvent('button_expand', { state });
      setAllCollapsed(false);
      setOpenSet(
        new Set(data.map((s, i) => (seatMatches(s) ? i : null)).filter((i) => i !== null))
      );
    } else {
      setAllCollapsed(true);
      setOpenSet(new Set());
    }
  };

  const onShare = () => {
    trackEvent('share_click', { url: window.location.href, location: 'seat_detail_floating' });
    if (navigator.share) {
      navigator.share({
        title: SHARE_TITLE,
        text: SHARE_TEXT,
        url: window.location.href,
      });
    }
  };

  const shareSeat = (cand) => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#s${cand.seatname}`;
    trackEvent('share_click', { url, seat: seatLabel(state, cand.seatname), location: 'seat_detail_button' });
    const payload = { title: SHARE_TITLE, text: SHARE_TEXT, url };
    if (navigator.share) {
      navigator.share(payload).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
  };

  // Title prefix: a specific constituency name when viewing a single seat,
  // otherwise the state label; both fall back to the bare suffix.
  const stateName = stateLabel(state);
  const seatRow = parliment
    ? data.find((s) => String(s.seatname) === String(parliment)) ||
      (data.length === 1 ? data[0] : null)
    : null;
  const metaPrefix = (seatRow && (seatRow.ednamec || seatRow.seatname)) || stateName;

  // True while any seat's result is still unofficial (is_o !== 1).
  const hasUnofficial =
    data.length > 0 && !data.every((s) => Number(s.is_o) === 1);

  if (!done)
    return (
      <>
        <PageMeta prefix={stateName} />
        <Loader />
      </>
    );

  return (
    <>
      <PageMeta prefix={metaPrefix} />
      <div className="div_main home-page">
        <div className="title_seat">
          <h1 className="title">
            {stateLabel(state)}
            {hasUnofficial && (
              <span className="title_unofficial">{tr('（非官方成績）')}</span>
            )}
          </h1>
        </div>

        <div className="chartNP">
          <ChartNP state={state} code={seatCode} />
        </div>

        {window.hideCurrentContestant === false &&<div className="table_party">
          <Party rows={partyRows} />
        </div>}

        

        <div className="seat_map">
          <h2>{stateLabel(state) + '- 地图'}</h2>
          <div className="legend_grid">
            {partyRows.map((row) => (
              <div className="legend_item" key={row.key}>
                <span className="legend_swatch" style={{ backgroundColor: row.color || '#cccccc' }} />
                <span>{row.party}</span>
              </div>
            ))}
          </div>
          <div className="map">
            {state === 'johor' && (
              <Johor code={seatCode} state={state} fills={mapFills} onSeatClick={handleMapSeatClick} />
            )}
            {state === 'kedah' && <Kedah code={seatCode} state={state} />}
            {state === 'kelantan' && <Kelantan code={seatCode} state={state} />}
            {state === 'kl' && <KL code={seatCode} state={state} />}
            {state === 'melaka' && (
              <Melaka code={seatCode} state={state} fills={mapFills} onSeatClick={handleMapSeatClick} />
            )}
            {state === 'negerisembilan' && (
              <Sembilan code={seatCode} state={state} fills={mapFills} onSeatClick={handleMapSeatClick} />
            )}
            {state === 'pahang' && <Pahang code={seatCode} state={state} />}
            {state === 'penang' && <Penang code={seatCode} state={state} />}
            {state === 'perak' && <Perak code={seatCode} state={state} />}
            {state === 'perlis' && <Perlis code={seatCode} state={state} />}
            {state === 'sabah' && (
              <Sabah code={seatCode} state={state} onSeatClick={handleMapSeatClick} />
            )}
            {state === 'sarawak' && <Sarawak code={seatCode} state={state} />}
            {state === 'selangor' && <Selangor code={seatCode} state={state} />}
            {state === 'terengganu' && <Terengganu code={seatCode} state={state} />}
          </div>
        </div>

        <VoteUpdates
          key={`${state}-${parliment}`}
          data={data}
          onSeatClick={handleUpdateClick}
        />

        <div className="home-ad-slot">
          <Gpt
            adUnit="/2163006/election2023_top"
            name="div-gpt-ad-1666944904129-0"
            size={[[300, 250], [336, 280], 'fluid']}
          />
        </div>

        <div className="button_expand">
          <button type="button" className="btn-secondary" onClick={toggleAll}>
            {allCollapsed ? '显示全部' : '隐藏全部'}
          </button> 
        </div>

        <div className="seat_filter">
          <div className="seat_filter_row">
            <button
              type="button"
              className={`filter_chip ${!filterAlliance ? 'is-active' : ''}`}
              onClick={() => selectAlliance(null)}
            >
              {tr('全部')}
            </button>
            {allianceOptions.map((opt) => {
              // Same convention as CandidateBar: uploaded logo first, then the
              // party-key URL; hide the img entirely if both fail to load.
              
              return (
                <button
                  type="button"
                  key={opt.key}
                  className={`filter_chip ${filterAlliance === opt.key ? 'is-active' : ''}`}
                  onClick={() => selectAlliance(opt.key)}
                >

                  {opt.logo && <img
                    className="filter_chip_logo"
                    src={opt.logo}
                    alt=""
                  />}
                  {tr(opt.label)}
                </button>
              );
            })}
          </div>
          {partyOptions.length > 0 && (
            <div className="seat_filter_row">
              <button
                type="button"
                className={`filter_chip ${!filterParty ? 'is-active' : ''}`}
                onClick={() => selectParty(null)}
              >
                {tr('全部')}
              </button>
              {partyOptions.map((opt) => (
                <button
                  type="button"
                  key={opt.key}
                  className={`filter_chip ${filterParty === opt.key ? 'is-active' : ''}`}
                  onClick={() => selectParty(opt.key)}
                >
                  {opt.logo && (
                    <img
                      className="filter_chip_logo"
                      src={opt.logo}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {tr(opt.label)}
                </button>
              ))}
            </div>
          )}
        </div>

        {(filterAlliance || filterParty) && visibleCount === 0 && (
          <p className="seat_filter_empty">{tr('暂无相关议席')}</p>
        )}

        <div className="seat_info">
          {data.map((cand, index) => {
            if (!seatMatches(cand)) return null;
            const winner =
              cand.candidate && cand.candidate.find((c) => c.status == 1);
            const accent = winner ? getBgColorByParty(winner.alliance_en) : '#cb0c13';
            const isOpen = openSet.has(index);
            return (
              <section  key={index} className="seat-section">
                {index % 11 === 0 && index !== 0 && (
                  <SeatInlineAd index={index} />
                )}
                <div
                  id={`s${cand.seatname}`}
                  className={`seat-collapse ${isOpen ? 'is-open' : ''}`}
                  style={{ borderLeftColor: accent }}
                >
                  <button
                    type="button"
                    className="seat-collapse__summary"
                    aria-expanded={isOpen}
                    onClick={() => togglePanel(index, cand.seatname)}
                  >
                    <span>
                      {cand.seatname} {cand.ednamec} {cand.edname}
                    </span>
                    <span className="seat-collapse__caret" aria-hidden="true">
                      {isOpen ? '▾' : '▸'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="seat-collapse__body">
                      {window.hideCurrentContestant === false && cand.is_o == '0' && parseInt(cand.total_votes) > 0 && (
                        <h3 className="hint_unofficial">*{tr('非官方成績')}</h3>
                      )}
                      {cand.showinfo === true && (
                        <SeatDemographics
                          regPercent={cand.reg_percent}
                          electorate={cand.electorate}
                        />
                      )}
                      {window.hideCurrentContestant === false && <SeatResultBody seat={cand} />}

                      <div className="seat_last_election">
                        <button
                          type="button"
                          className="btn-secondary seat_last_btn"
                          aria-expanded={lastOpenSet.has(index)}
                          onClick={() => toggleLast(index, cand.seatname)}
                        >
                          {lastOpenSet.has(index)
                            ? tr('隐藏上届成绩')
                            : tr('查看上届成绩')}
                        </button>
                        <div className="seat_btn_row">
                          <button
                            type="button"
                            className="btn-secondary seat_last_btn seat_news_btn"
                            onClick={() => {
                              trackEvent('seat_related_news', {
                                seat: seatLabel(state, cand.seatname),
                                tag: cand.ednamec,
                              });
                              setNewsTag(cand.ednamec);
                            }}
                          >
                            {tr('相关新闻')}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary seat_last_btn seat_news_btn seat_share_btn"
                            style={{ backgroundColor: 'var(--color-brand-line)' }}
                            onClick={() => shareSeat(cand)}
                          >
                            <IoShareSocialOutline aria-hidden="true" />
                            {tr('分享选区资讯')}
                          </button>
                        </div>
                        {lastOpenSet.has(index) && (
                          <div className="seat_last_body">
                            {lastLoading ? (
                              <p className="seat_last_hint">{tr('载入中…')}</p>
                            ) : lastData && lastData[cand.seatname] ? (
                              <>
                                <h4 className="seat_last_title">{tr('上届成绩')}</h4>
                                <SeatResultBody seat={lastData[cand.seatname]} />
                              </>
                            ) : (
                              <p className="seat_last_hint">{tr('暂无上届数据')}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {navigator.share && (
          <button
            type="button"
            className="floating-share-button"
            onClick={onShare}
            aria-label="Share"
          />
        )}
      </div>
      <SeatInfoPanel
        seat={selectedSeatData}
        seatName={selectedSeatName}
        onClose={() => setSelectedSeatName(null)}
        onViewDetail={handleViewSeatSection}
      />
      <PartyArticlesPanel
        tag={newsTag}
        location="seat_detail"
        onClose={() => setNewsTag(null)}
      />
      <RelatedNewsPanel state={state} />
      <NewsRollDrawer />
      <StateFlagsNav />
    </>
  );
};

export default SeatsDetail;
